"use client";

/**
 * Pricing Card Component
 *
 * Dark-themed pricing card with per-plan gradient overlays.
 * Featured (Grow) card gets pink border + "MOST POPULAR" badge above.
 */
import { useCallback, useRef, type ReactNode, type ComponentType } from "react";
import { T, Var } from "gt-next";
import { TrendingUpIcon, type TrendingUpIconHandle } from "@/components/ui/trending-up";
import { SparklesIcon, type SparklesIconHandle } from "@/components/ui/sparkles-icon";
import { RocketIcon, type RocketIconHandle } from "@/components/ui/rocket-icon";
import { Spinner } from "@/components/ui/spinner";
import { ClockIcon } from "@/components/animate-ui/icons/clock";
import { UsersRoundIcon } from "@/components/animate-ui/icons/users-round";
import { ChartLineIcon } from "@/components/animate-ui/icons/chart-line";
import { UsersIcon } from "@/components/animate-ui/icons/users";
import { SparklesIcon as SparklesAnimIcon } from "@/components/animate-ui/icons/sparkles";
import { SlidersHorizontalIcon } from "@/components/animate-ui/icons/sliders-horizontal";
import { RadioTowerIcon } from "@/components/animate-ui/icons/radio-tower";
import { SparkleIcon } from "@/components/animate-ui/icons/sparkle";
import { GaugeIcon } from "@/components/animate-ui/icons/gauge";
import { ChartBarIncreasingIcon } from "@/components/animate-ui/icons/chart-bar-increasing";
import { UserRoundIcon } from "@/components/animate-ui/icons/user-round";
import { SendIcon } from "@/components/animate-ui/icons/send";
import { StarIcon } from "@/components/animate-ui/icons/star";
import { MessageCircleIcon } from "@/components/animate-ui/icons/message-circle";
import { AnimateIcon } from "@/components/animate-ui/icons/icon";
import { TouchTooltip } from "@/components/ui/tooltip";

// ── i18n: Static lookup tables for translatable strings ──────────────────
// gt-next's <T> component can only extract STATIC children at build time.
// Since plans-data.ts passes descriptions/features as variables, we create
// indexed lookup objects with static <T> children so the extractor finds them.

/** Plan description translations keyed by plan name */
const PLAN_DESCRIPTION_T: Record<string, ReactNode> = {
  Go: <T>Steady growth for new Instagram accounts.</T>,
  Grow: <T>Unlimited growth & AI-powered targeting.</T>,
  Glory: <T>Maximum growth speed & fully managed.</T>
};

/** CTA button label translations */
const BUTTON_LABEL_T: Record<string, ReactNode> = {
  "Start growing free": <T>Start growing free</T>,
  "Start Your Growth": <T>Start Your Growth</T>,
  "Start your growth": <T>Start your growth</T>,
  "Get started": <T>Get started</T>
};

/** Section header translations keyed by plan name */
const SECTION_HEADER_T: Record<string, ReactNode> = {
  Go: <T>What's included:</T>,
  Grow: <T>Everything in Go, plus:</T>,
  Glory: <T>Everything in Grow, plus:</T>
};

/**
 * Feature item translations keyed by a unique substring in the HTML string.
 * The key is matched against the raw HTML feature string from plans-data.
 * Features using dangerouslySetInnerHTML for HTML formatting still need
 * translated text, so we provide full ReactNode replacements.
 */
const FEATURE_T: Record<string, ReactNode> = {
  // Go features
  "1,000 - 2,000": <T><b className="follower-count">1,000 - 2,000</b> real followers/mo</T>,
  "Growth starts within 24 hours": <T>Growth starts within 24 hours</T>,
  "100% real, engaged followers": <T>100% real, engaged followers</T>,
  "Live growth dashboard": <T>Live growth dashboard</T>,
  // Grow features
  "2,500 - 3,500": <T><b className="follower-count">2,500 - 3,500</b> real followers/mo</T>,
  "2.75x faster growth": <T><span className="outfame-max-gradient">Outfame MAX{"\u2122"}</span> {"\u2014"} 2.75x faster growth</T>,
  "Competitor audience targeting": <T>Competitor audience targeting</T>,
  "Smart niche matching": <T>Smart niche matching</T>,
  "Auto profile optimizer": <T>Auto profile optimizer</T>,
  "15</b> AI-generated posts/mo": <T><b>15</b> AI-generated posts/mo</T>,
  "Real-time analytics": <T>Real-time analytics</T>,
  // Glory features
  "4,000+": <T><b className="follower-count">4,000+</b> guaranteed real followers/mo</T>,
  "Dedicated account manager": <T>Dedicated account manager</T>,
  "boost-post-gradient": <T><span className="boost-post-gradient">Boost Post</span> {"\u2014"} Game the algorithm</T>,
  "Viral content templates": <T>Viral content templates</T>,
  "30</b> AI-generated posts/mo": <T><b>30</b> AI-generated posts/mo</T>,
  "Weekly growth reports sent to you": <T>Weekly growth reports sent to you</T>,
  "Priority 24/7 support": <T>Priority 24/7 support</T>
};

/** Find translated ReactNode for a feature HTML string */
function getFeatureTranslation(featureHtml: string): ReactNode | null {
  for (const [key, node] of Object.entries(FEATURE_T)) {
    if (featureHtml.includes(key)) return node;
  }
  return null;
}

/** Handle for controlling animated icons */
export interface AnimatedIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}
export interface PricingPlan {
  name: string;
  price: {
    Monthly: string;
    Yearly: string;
    YearlyTotal?: string;
  };
  firstMonthMonthlyPrice?: string;
  /** Price shown as strikethrough on monthly period */
  monthlyRetailPrice?: string;
  description: string;
  features: string[];
  planIDs: string[];
  featured?: boolean;
  button?: {
    label: string;
    href: string;
  };
  /** Animated icon component (receives ref for animation control) */
  icon?: ReactNode;
}
interface PricingCardProps {
  plan: PricingPlan;
  activePeriod: "Monthly" | "Yearly";
  variant?: "light" | "dark" | "featured";
  /** Size variant - "compact" for modal use with reduced padding */
  size?: "default" | "compact";
  /** Context - affects CTA behavior */
  context?: "landing" | "modal";
  onPlanSelected?: (plan: PricingPlan) => void;
  /** Show loading spinner on CTA button */
  isLoading?: boolean;
  /** Apply discount percentage to price display */
  discountPercent?: number;
  /** Hide the struck-through month-2+ renewal price (public pages never show an ongoing price) */
  hideRenewalPrice?: boolean;
}

/** Icon size for plan icons */
const ICON_SIZE = 28;
const ICON_SIZE_COMPACT = 24;

/** Accent colors per plan */
const PLAN_ACCENT = {
  Go: {
    icon: "text-blue-400",
    check: "#60a5fa",
    strike: {
      from: "#60a5fa",
      to: "#93c5fd"
    },
    badge: "bg-blue-400/10 text-blue-300"
  },
  Grow: {
    icon: "text-pink",
    check: "#ffa3d1",
    strike: {
      from: "#ff6bb3",
      to: "#ffa3d1"
    },
    badge: "bg-pink/10 text-pink"
  },
  Glory: {
    icon: "text-purple-400",
    check: "#c084fc",
    strike: {
      from: "#a855f7",
      to: "#c084fc"
    },
    badge: "bg-purple-400/10 text-purple-300"
  }
} as const;

/** Gradient backgrounds per plan */
const PLAN_GRADIENT = {
  Go: "[background:linear-gradient(180deg,rgba(12,192,251,0)_0%,rgba(12,192,251,0.03)_40%,rgba(12,192,251,0.18)_100%),#141414]",
  Grow: "[background:linear-gradient(180deg,rgba(255,163,209,0)_0%,rgba(255,163,209,0.03)_40%,rgba(255,163,209,0.18)_100%),#141414]",
  Glory: "[background:linear-gradient(180deg,rgba(168,85,247,0)_0%,rgba(168,85,247,0.03)_40%,rgba(168,85,247,0.18)_100%),#141414]"
} as const;

/** Tooltip copy for each feature row - benefits-focused, outcome-oriented */
const FEATURE_TOOLTIPS: Record<string, {
  title: string;
  description: string;
}> = {
  "24 hours": {
    title: "Fast Results",
    description: "Your account starts gaining real followers within the first 24 hours of activation."
  },
  "real, engaged followers": {
    title: "Real People, Real Engagement",
    description: "Every follower is a real person genuinely interested in your content. No bots, no fakes, ever."
  },
  "growth dashboard": {
    title: "Live Growth Dashboard",
    description: "Track every new follower, like, and engagement metric in real time from your personal dashboard."
  },
  "outfame-max-gradient": {
    title: "Outfame MAX\u2122",
    description: "AI-powered growth engine that analyzes top-performing accounts in your niche and replicates their growth patterns. 2.75x more followers on average."
  },
  "Competitor audience": {
    title: "Competitor Targeting",
    description: "We find people who follow and engage with your competitors\u2014and put your profile in front of them."
  },
  "niche matching": {
    title: "Smart Niche Matching",
    description: "AI identifies the communities, hashtags, and accounts most relevant to your niche for laser-focused growth."
  },
  "profile optimizer": {
    title: "Auto Profile Optimizer",
    description: "AI analyzes your bio, highlights, and content to suggest changes that increase your follow-back rate."
  },
  "AI-generated posts": {
    title: "AI Content Creation",
    description: "Get scroll-stopping posts created for you automatically\u2014designed for your niche and optimized for engagement."
  },
  "Real-time analytics": {
    title: "Real-Time Analytics",
    description: "See exactly what\u2019s working: follower sources, engagement trends, and growth velocity\u2014updated live."
  },
  "Dedicated account manager": {
    title: "Your Dedicated Manager",
    description: "A real growth expert assigned to your account. They handle strategy, targeting, and optimization\u2014you just watch it grow."
  },
  "boost-post-gradient": {
    title: "Boost Post",
    description: "Real engagement signals delivered in the critical first 30 minutes after posting to trigger the algorithm and maximize your reach. 3x higher reach on average."
  },
  "Viral content": {
    title: "Viral Content Templates",
    description: "Access a library of proven-to-perform content formats and ideas tailored to your niche. Post with confidence."
  },
  "growth reports": {
    title: "Weekly Growth Reports",
    description: "Every week, a detailed PDF report lands in your inbox\u2014covering new followers, engagement, and what to do next."
  },
  "24/7 support": {
    title: "Priority 24/7 Support",
    description: "Skip the queue. Get direct access to our team anytime\u2014day or night\u2014with response times under 2 hours."
  }
};

/** Feature icon mapping: plan name → array of icons for features[1:] */
const FEATURE_ICONS: Record<string, ComponentType<any>[]> = {
  Go: [ClockIcon, UsersRoundIcon, ChartLineIcon],
  Grow: [GaugeIcon, UsersIcon, SparklesAnimIcon, SlidersHorizontalIcon, SparkleIcon, ChartBarIncreasingIcon],
  Glory: [GaugeIcon, RadioTowerIcon, UserRoundIcon, StarIcon, SparkleIcon, SendIcon, MessageCircleIcon]
};

/** Find tooltip for a feature by matching keywords in the HTML string */
function getFeatureTooltip(featureHtml: string): {
  title: string;
  description: string;
} | null {
  for (const [keyword, tooltip] of Object.entries(FEATURE_TOOLTIPS)) {
    if (featureHtml.includes(keyword)) return tooltip;
  }
  return null;
}
export default function PricingCard({
  plan,
  activePeriod,
  variant = "light",
  size = "default",
  context = "landing",
  onPlanSelected,
  isLoading = false,
  discountPercent = 0,
  hideRenewalPrice = false
}: PricingCardProps) {
  const isFeatured = variant === "featured" || plan.featured;
  const isCompact = size === "compact";
  const planName = plan.name as keyof typeof PLAN_ACCENT;

  // Calculate prices
  const basePrice = activePeriod === "Monthly" ? plan.firstMonthMonthlyPrice || plan.price.Monthly : plan.price.Yearly;
  const currentPrice = discountPercent > 0 ? Math.round(parseInt(basePrice) * (1 - discountPercent / 100)) : parseInt(basePrice);

  // Strikethrough price: Same retail price for both periods (shows max savings)
  const strikethroughPrice = parseInt(plan.monthlyRetailPrice || plan.price.Monthly);

  // Savings for yearly pill (total annual savings: monthly price × 12 minus yearly total)
  const monthlyCompare = parseInt(plan.monthlyRetailPrice || plan.firstMonthMonthlyPrice || plan.price.Monthly);
  const yearlyMonthly = parseInt(plan.price.Yearly);
  const yearlySavings = monthlyCompare * 12 - yearlyMonthly * 12;
  const yearlySavingsFormatted = `$${yearlySavings.toLocaleString("en-US")}`;

  // Calculate per-day pricing (always round DOWN to 1 decimal)
  const perDay = Math.floor(currentPrice / 30 * 10) / 10;

  // Separate typed refs for each icon type
  const goIconRef = useRef<TrendingUpIconHandle>(null);
  const growIconRef = useRef<SparklesIconHandle>(null);
  const gloryIconRef = useRef<RocketIconHandle>(null);
  const handleClick = () => {
    if (onPlanSelected && !isLoading) {
      onPlanSelected(plan);
    }
  };
  const getActiveRef = () => {
    switch (plan.name) {
      case "Go":
        return goIconRef;
      case "Grow":
        return growIconRef;
      case "Glory":
        return gloryIconRef;
      default:
        return null;
    }
  };
  const handleMouseEnter = useCallback(() => {
    getActiveRef()?.current?.startAnimation();
  }, [plan.name]);
  const handleMouseLeave = useCallback(() => {
    getActiveRef()?.current?.stopAnimation();
  }, [plan.name]);
  const iconSize = isCompact ? ICON_SIZE_COMPACT : ICON_SIZE;
  const renderIcon = () => {
    switch (plan.name) {
      case "Go":
        return <TrendingUpIcon ref={goIconRef} size={iconSize} />;
      case "Grow":
        return <SparklesIcon ref={growIconRef} size={iconSize} />;
      case "Glory":
        return <RocketIcon ref={gloryIconRef} size={iconSize} autoAnimateInterval={5500} />;
      default:
        return null;
    }
  };

  // Size-based styles
  const cardPadding = isCompact ? "p-5 md:p-6" : "p-6 md:p-8";
  const cardGap = isCompact ? "gap-3 md:gap-4" : "gap-5 md:gap-6";
  const titleSize = isCompact ? "text-[17px] md:text-[18px]" : "text-[18px] md:text-[20px]";
  const descriptionSize = isCompact ? "text-[14px]" : "text-[14px]";
  const priceSize = isCompact ? "text-[44px] md:text-[50px]" : "text-[48px] md:text-[64px]";
  const featureSize = isCompact ? "text-[14px]" : "text-[14px]";
  const featureGap = isCompact ? "gap-2" : "gap-2.5 md:gap-3";
  const checkSize = isCompact ? "h-4 w-4" : "h-[16px] w-[16px] md:h-[18px] md:w-[18px]";

  // Determine if we should use callback or link
  const shouldUseCallback = context === "modal" || !!onPlanSelected;

  // CTA button color per plan (includes shadow effects)
  const getButtonClass = () => {
    if (planName === "Grow") return "bg-pink text-black shadow-[0px_6px_14px_0px_rgba(255,163,209,0.15),0px_3px_6px_0px_rgba(255,163,209,0.15),inset_0px_-3px_rgba(0,0,0,0.43)]";
    if (planName === "Glory") return "bg-gradient-to-r from-purple-500 to-purple-700 text-white shadow-[0px_6px_14px_0px_rgba(168,85,247,0.18),0px_3px_6px_0px_rgba(168,85,247,0.18),inset_0px_-3px_rgba(0,0,0,0.43)]";
    return "bg-white text-black shadow-[inset_0px_-3px_rgba(0,0,0,0.15)]";
  };
  const accent = PLAN_ACCENT[planName] || PLAN_ACCENT.Go;
  const gradient = PLAN_GRADIENT[planName] || PLAN_GRADIENT.Go;
  const cardContent = <div data-plan={planName.toLowerCase()} className={`grain group relative flex h-full flex-col ${cardGap} rounded-[20px] ${cardPadding} ${gradient} shadow-[0px_8px_24px_0px_rgba(0,0,0,0.12)]`} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            {/* Header Section - min-height ensures CTA alignment across cards */}
            <div className={`flex flex-col gap-2 ${isCompact ? "" : "min-h-[76px] md:min-h-[72px]"}`}>
                {/* Plan name + Icon + Per-day badge (inline) */}
                <div className="flex items-center gap-2 md:gap-2.5">
                    <span className={accent.icon}>{renderIcon()}</span>
                    <h3 className={`${titleSize} font-semibold text-white`}>{plan.name}</h3>
                    {/* Limited Offer badge - only on Grow and Glory */}
                    {planName !== "Go" && <span className="flex items-center gap-1 rounded-md px-2 py-[4px] text-[11px] font-bold uppercase text-white" style={{
          background: "radial-gradient(circle, rgb(249, 32, 209) 0%, rgb(237, 21, 114) 100%)"
        }}>
                            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M5 3H3.5C3.22386 3 3 2.77614 3 2.5C3 2.22386 3.22386 2 3.5 2H20.5C20.7761 2 21 2.22386 21 2.5C21 2.77614 20.7761 3 20.5 3H19V7.20906C19 7.71534 18.7446 8.18745 18.3209 8.46451L12.9137 12L18.7736 15.8315C18.9149 15.9239 19 16.0812 19 16.25V21H20.5C20.7761 21 21 21.2239 21 21.5C21 21.7761 20.7761 22 20.5 22H3.5C3.22386 22 3 21.7761 3 21.5C3 21.2239 3.22386 21 3.5 21H5V16.25C5 16.0812 5.08513 15.9239 5.22638 15.8315L11.0863 12L5.67913 8.46451C5.25539 8.18745 5 7.71534 5 7.20906V3ZM6 3V7H18V3H6ZM18 18V16.5205L12 12.5974L6 16.5205V18H18Z" fill="currentColor" />
                            </svg>
                            <T>Limited Seats</T>
                        </span>}
                    {/* Per-day badge - plan-colored */}
                    <span className={`flex items-center rounded-md px-2 py-[3px] text-[12px] font-bold tabular-nums ${accent.badge}`}><T>$<Var name="perDay">{perDay}</Var>/day</T></span>
                </div>
                {/* Description */}
                <p className={`${descriptionSize} text-balance leading-normal text-white/60 md:text-wrap`}>{PLAN_DESCRIPTION_T[plan.name] || plan.description}</p>
            </div>

            {/* Price Section */}
            <div className="flex items-center gap-3">
                {/* Strikethrough price (month-2+ renewal price) — hidden on public pages */}
                {!hideRenewalPrice && <span className="relative inline-block opacity-25">
                        <span className="font-mosans text-[35px] leading-none text-white">
                            ${strikethroughPrice}
                        </span>
                        {/* Strikethrough line */}
                        <span className="absolute left-[-3px] right-[-3px] top-1/2 h-[5px] -translate-y-1/2 rounded-full bg-white" />
                    </span>}
                {/* Current price */}
                <span className={`font-mosans ${priceSize} leading-none text-white`}>${currentPrice}</span>
                {/* /mo + billing label */}
                <div className="flex flex-col">
                    <span className="text-[16px] font-semibold text-white/60"><T>/mo</T></span>
                    <span className="text-[14px] text-white/40">{activePeriod === "Yearly" ? <T>billed yearly</T> : <T>billed monthly</T>}</span>
                </div>
            </div>

            {/* CTA Button */}
            <div className="pt-2 md:pt-3">
                {shouldUseCallback ? <button onClick={handleClick} disabled={isLoading} className={`h-14 md:h-[50px] w-full rounded-xl pb-0.5 text-[16px] font-semibold transition-[opacity,transform] duration-200 hover:opacity-90 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-70 ${getButtonClass()}`}>
                        {isLoading ? <Spinner size="sm" className="mx-auto" /> : context === "modal" ? <T>Start your growth</T> : BUTTON_LABEL_T[plan.button?.label || "Get started"] || plan.button?.label || <T>Get started</T>}
                    </button> : <button onClick={() => window.location.href = plan.button?.href || "/setup"} className={`h-14 md:h-[50px] w-full rounded-xl pb-0.5 text-[16px] font-semibold transition-[opacity,transform] duration-200 hover:opacity-90 active:scale-[0.98] ${getButtonClass()}`}>
                        {BUTTON_LABEL_T[plan.button?.label || "Get started"] || plan.button?.label || <T>Get started</T>}
                    </button>}

                {/* Savings pill (yearly only) */}
                <div className={`overflow-hidden transition-[max-height,opacity,margin] duration-300 ${activePeriod === "Yearly" ? "mt-3 max-h-12 opacity-100" : "mt-0 max-h-0 opacity-0"}`}>
                    <div className="flex items-center justify-center gap-1.5 rounded-xl bg-white/5 px-3 py-2 text-xs font-medium text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" className="h-4 w-4 shrink-0">
                            <path stroke="#EBBE7A" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.75 6.625v-.75m0 7.5v.75m1.624-6.375C11.05 7.302 10.444 7 9.75 7h-.208c-.92 0-1.667.597-1.667 1.333v.058c0 .526.372 1.008.96 1.243l1.83.732c.588.235.96.717.96 1.243 0 .768-.778 1.391-1.738 1.391H9.75c-.694 0-1.3-.302-1.624-.75M16.5 10A6.75 6.75 0 1 1 3 10a6.75 6.75 0 0 1 13.5 0Z" />
                        </svg>
                        <T>Save <Var name="savings">{yearlySavingsFormatted}</Var> compared to monthly</T>
                    </div>
                </div>
            </div>

            {/* Highlighted Followers Count */}
            <TouchTooltip content={<>
                        <p className="text-[14px] font-semibold tracking-[-0.01em] text-gray-900"><T>Monthly Followers</T></p>
                        <p className="mt-1.5 text-[14px] leading-[1.5] text-gray-600"><T>Estimated new monthly followers with full refund if no net-positive growth after 30 days.</T></p>
                    </>} side="top" variant="light" contentClassName="max-w-[280px] rounded-xl px-4 py-3">
                <div className="flex cursor-pointer items-center gap-2.5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" className="h-[20px] w-[20px] flex-none" stroke={accent.check} strokeWidth="1.75">
                        <path d="m15.2613 5.21348c.0004.00034.0007.00063.0012.00083 1.6726.77082 3.0381 2.10998 3.8684 3.79384.8304 1.68405 1.0752 3.61095.6935 5.45875-.3816 1.8478-1.3667 3.5046-2.7906 4.6937-1.4239 1.189-3.2004 1.8383-5.0327 1.8394-1.5673-.0001-3.10007-.4747-4.40804-1.3651-1.30798-.8903-2.33355-2.1571-2.94942-3.6431s-.79491-3.1257-.5149-4.7157c.207-1.1754.65813-2.28536 1.31694-3.25609.35862-.52842 1.11722-.47193 1.55629-.00819.23113.24412.47658.47321.73491.68598.57609.47449 1.38039.01532 1.52235-.7174.14655-.7564.38742-1.49243.71822-2.18931.42495-.89528.99055-2.27822 1.67185-3.25868.3297-.47452.9818-.39821 1.3005.08381.6597.99795 1.442 2.16196 2.3105 2.59649.0004.00019.0007.00045.001.00077z" />
                        <path d="m12 18c.9514-.0004 1.8671-.3623 2.5616-1.0125.6945-.6503 1.1159-1.5402 1.1788-2.4895s-.2373-1.887-.8399-2.6232c-.4065-.4966-.93-.8764-1.5171-1.1098-.5429-.2157-1.154-.0196-1.5246.432-.456.5557-.8081 1.1888-1.0397 1.8686-.1812.5319-.7231.9042-1.23739.6777-.59786-.2633-1.35339-.0106-1.32978.6422.00534.1476.0194.2951.04225.4417.09127.5859.32016 1.1418.66788 1.622.34772.4803.80436.8712 1.33244 1.1408.5281.2696 1.1126.4101 1.7055.41z" />
                    </svg>
                    <span className={`${featureSize} font-bold text-white border-b border-dashed border-white/30 pb-[1px] [&_.follower-count]:font-bold`} style={{
          ['--accent-color' as any]: accent.check
        }}>
                        <span className="[&_.follower-count]:text-[var(--accent-color)]">{getFeatureTranslation(plan.features[0]) || <span dangerouslySetInnerHTML={{
              __html: plan.features[0]
            }} />}</span>
                    </span>
                </div>
            </TouchTooltip>

            {/* Persistent refund/guarantee chip (always visible — not hover-gated) */}
            <div className="flex items-center gap-1.5 -mt-1 text-[12px] font-medium text-white/55">
                <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 2.5l7 3v5.5c0 4.5-3 8.2-7 9.5-4-1.3-7-5-7-9.5V5.5l7-3z" />
                    <path d="M9 12l2 2 4-4" />
                </svg>
                <span><T>Full refund if no net growth in 30 days</T></span>
            </div>

            {/* Separator */}
            <div className="h-px w-full bg-white/10" />

            {/* Features List */}
            <div className={`flex flex-1 flex-col ${isCompact ? "gap-3" : "gap-4"}`}>
                {/* Section header */}
                <p className={`${featureSize} font-semibold text-white`}>
                    {plan.name === "Go" && <T>What's included:</T>}
                    {plan.name === "Grow" && <T>Everything in Go, plus:</T>}
                    {plan.name === "Glory" && <T>Everything in Grow, plus:</T>}
                </p>
                {/* Feature items */}
                <ul className={`flex flex-col ${featureGap}`}>
                    {plan.features.slice(1).map((feature, index) => {
          const icons = FEATURE_ICONS[plan.name];
          const IconComponent = icons?.[index];
          if (IconComponent) {
            const tooltip = getFeatureTooltip(feature);
            const translatedFeature = getFeatureTranslation(feature);
            const featureItem = <AnimateIcon key={index} animateOnHover={true} asChild>
                                    <li className="flex items-center gap-3">
                                        <IconComponent size={isCompact ? 16 : 18} className="shrink-0" style={{
                  color: accent.check
                }} />
                                        <span className={`${featureSize} text-white [&_.follower-count]:text-white border-b border-dashed border-white/15 pb-[1px]`}>
                                            {translatedFeature || <span dangerouslySetInnerHTML={{
                    __html: feature
                  }} />}
                                        </span>
                                    </li>
                                </AnimateIcon>;
            if (tooltip) {
              return <TouchTooltip key={index} content={<>
                                                <p className="text-[14px] font-semibold tracking-[-0.01em] text-gray-900">{tooltip.title}</p>
                                                <p className="mt-1.5 text-[14px] leading-[1.5] text-gray-600">{tooltip.description}</p>
                                            </>} side="top" variant="light" contentClassName="max-w-[280px] rounded-xl px-4 py-3">
                                        <div className="cursor-pointer">{featureItem}</div>
                                    </TouchTooltip>;
            }
            return featureItem;
          }
          const tooltip_0 = getFeatureTooltip(feature);
          const translatedFeature2 = getFeatureTranslation(feature);
          const featureItem_0 = <li key={index} className="flex items-center gap-3">
                                <svg className={`${checkSize} shrink-0`} viewBox="0 0 20 20" fill="none">
                                    <circle cx="10" cy="10" r="10" fill={accent.check} />
                                    <path d="M6 10L8.5 12.5L14 7" stroke="#141414" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span className={`${featureSize} text-white [&_.follower-count]:text-white border-b border-dashed border-white/15 pb-[1px]`}>
                                    {translatedFeature2 || <span dangerouslySetInnerHTML={{
                __html: feature
              }} />}
                                </span>
                            </li>;
          if (tooltip_0) {
            return <TouchTooltip key={index} content={<>
                                            <p className="text-[14px] font-semibold tracking-[-0.01em] text-gray-900">{tooltip_0.title}</p>
                                            <p className="mt-1.5 text-[14px] leading-[1.5] text-gray-600">{tooltip_0.description}</p>
                                        </>} side="top" variant="light" contentClassName="max-w-[280px] rounded-xl px-4 py-3">
                                    <div className="cursor-pointer">{featureItem_0}</div>
                                </TouchTooltip>;
          }
          return featureItem_0;
        })}
                </ul>
            </div>
        </div>;

  // Featured card: pink background wrapper with badge strip (HiggsField pattern)
  if (isFeatured) {
    return <div className="h-full flex flex-col rounded-[22px] bg-pink">
                {/* Badge strip - dark text on pink background */}
                <div className="flex shrink-0 items-center justify-center gap-1.5 py-2.5">
                    <svg className="h-3.5 w-3.5 text-ink" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M6.56218 3.52331C6.89119 3.18855 7.34089 3 7.81027 3H16.186C16.6554 3 17.1051 3.18855 17.4341 3.52331L22.5881 8.76722C23.2614 9.45231 23.2567 10.5521 22.5774 11.2313L13.2356 20.5732C12.5521 21.2566 11.4441 21.2566 10.7607 20.5732L1.41881 11.2313C0.73957 10.5521 0.734815 9.45231 1.40816 8.76722L6.56218 3.52331ZM9.02845 7.21967C9.32135 7.51256 9.32135 7.98744 9.02845 8.28033L7.30878 10L9.02845 11.7197C9.32135 12.0126 9.32135 12.4874 9.02845 12.7803C8.73556 13.0732 8.26069 13.0732 7.96779 12.7803L5.71779 10.5303C5.4249 10.2374 5.4249 9.76256 5.71779 9.46967L7.96779 7.21967C8.26069 6.92678 8.73556 6.92678 9.02845 7.21967Z" />
                    </svg>
                    <span className="text-xs font-bold uppercase tracking-wide text-ink"><T>Most Popular</T></span>
                </div>
                {/* Inner card - inset to show pink as border */}
                <div className="mx-[3px] mb-[2px] flex-1 overflow-hidden rounded-[20px]">
                    {cardContent}
                </div>
            </div>;
  }
  return cardContent;
}